// Passkey (WebAuthn) — klienthjelpere. Bruker @simplewebauthn/browser for base64url/ArrayBuffer-håndtering.
import axios from 'axios';
import { startAuthentication, startRegistration, browserSupportsWebAuthn, platformAuthenticatorIsAvailable } from '@simplewebauthn/browser';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export interface PasskeyInfo { id: string; label: string; created_at: string; last_used_at: string | null; transports: string[]; backed_up: boolean; device_type: string; rp_id: string }

export const passkeysSupported = () => typeof window !== 'undefined' && browserSupportsWebAuthn();
export const platformPasskeyAvailable = () => platformAuthenticatorIsAvailable().catch(() => false);

export async function fetchPasskeyConfig(): Promise<{ enabled: boolean; rp_id: string | null }> {
  try { const { data } = await axios.get(`${API}/auth/passkey/config`); return data; }
  catch { return { enabled: false, rp_id: null }; }
}

export async function registerPasskey(label?: string): Promise<PasskeyInfo> {
  const { data } = await axios.post(`${API}/auth/passkey/register/options`, {});
  const credential = await startRegistration({ optionsJSON: data.options });
  const res = await axios.post(`${API}/auth/passkey/register/verify`, { ceremony_id: data.ceremony_id, credential, label: label || null });
  return res.data.passkey;
}

export async function loginWithPasskey(rememberMe = false): Promise<{ access_token: string; user: any }> {
  const { data } = await axios.post(`${API}/auth/passkey/login/options`, {});
  const credential = await startAuthentication({ optionsJSON: data.options });
  const res = await axios.post(`${API}/auth/passkey/login/verify`, { ceremony_id: data.ceremony_id, credential, remember_me: rememberMe });
  return res.data;
}

export const listPasskeys = () => axios.get(`${API}/auth/passkey/list`).then(r => r.data.passkeys as PasskeyInfo[]);
export const renamePasskey = (id: string, label: string) => axios.patch(`${API}/auth/passkey/${id}`, { label });
export const deletePasskey = (id: string) => axios.delete(`${API}/auth/passkey/${id}`);

// Brukervennlig feilmelding fra WebAuthn-unntak / API-feil
export function passkeyErrorMessage(e: any, fallback: string): string {
  const name = e?.name || '';
  if (name === 'NotAllowedError') return 'Avbrutt eller ikke godkjent. Prøv igjen.';
  if (name === 'InvalidStateError') return 'Denne passkeyen er allerede registrert på kontoen.';
  if (name === 'NotSupportedError') return 'Nettleseren eller enheten støtter ikke passkeys.';
  if (name === 'SecurityError') return 'Passkeys kan ikke brukes fra dette domenet.';
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
}
